import React, { useState, useMemo } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  BarChart3, 
  Download, 
  Calendar as CalendarIcon, 
  TrendingUp, 
  Clock, 
  Users,
  Target,
  PieChart,
  Activity,
  FileText,
  Filter,
  Share2,
  Mail,
  Printer
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TaskStatus, TaskPriority, ProjectStatus } from '@/types';

export const Reports: React.FC = () => {
  const { projects, tasks } = useProject();
  const { user } = useAuth();
  const [selectedReport, setSelectedReport] = useState<'productivity' | 'projects' | 'time' | 'team'>('productivity');
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [selectedProject, setSelectedProject] = useState<string>('all');

  // Mock users for reports
  const mockUsers = [
    { id: '1', name: 'Admin User', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face' },
    { id: '2', name: 'Manager User', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=32&h=32&fit=crop&crop=face' },
    { id: '3', name: 'Collaborator User', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face' }
  ];

  // Generate productivity data
  const productivityData = useMemo(() => {
    const data = [];
    const now = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      const tasksCompleted = Math.floor(Math.random() * 8) + 1;
      const tasksCreated = Math.floor(Math.random() * 12) + tasksCompleted;
      
      data.push({
        date,
        tasksCompleted,
        tasksCreated,
        productivity: Math.round((tasksCompleted / tasksCreated) * 100)
      });
    }
    
    return data;
  }, []);

  // Generate project performance data
  const projectPerformance = useMemo(() => {
    return projects.map(project => {
      const projectTasks = tasks.filter(t => t.projectId === project.id);
      const completedTasks = projectTasks.filter(t => t.status === TaskStatus.DONE).length;
      const totalHours = projectTasks.reduce((sum, task) => sum + task.estimatedHours, 0);
      const actualHours = projectTasks.reduce((sum, task) => sum + (task.actualHours || 0), 0);
      
      return {
        ...project,
        tasksCount: projectTasks.length,
        completedTasks,
        completionRate: projectTasks.length > 0 ? Math.round((completedTasks / projectTasks.length) * 100) : 0,
        totalHours,
        actualHours,
        efficiency: totalHours > 0 ? Math.round((totalHours / Math.max(actualHours, 1)) * 100) : 100
      };
    });
  }, [projects, tasks]);

  // Generate time tracking data
  const timeTrackingData = useMemo(() => {
    const data = [];
    const daysOfWeek = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    
    for (let i = 0; i < 7; i++) {
      const hours = Math.floor(Math.random() * 8) + 1;
      data.push({
        day: daysOfWeek[i],
        hours,
        efficiency: Math.floor(Math.random() * 30) + 70
      });
    }
    
    return data;
  }, []);

  // Generate team performance data
  const teamPerformance = useMemo(() => {
    return mockUsers.map(user => {
      const userTasks = tasks.filter(t => t.assignedTo === user.id);
      const completedTasks = userTasks.filter(t => t.status === TaskStatus.DONE).length;
      const totalHours = userTasks.reduce((sum, task) => sum + (task.actualHours || 0), 0);
      
      return {
        ...user,
        tasksCount: userTasks.length,
        completedTasks,
        completionRate: userTasks.length > 0 ? Math.round((completedTasks / userTasks.length) * 100) : 0,
        totalHours,
        productivity: Math.floor(Math.random() * 20) + 80
      };
    });
  }, [tasks]);

  const exportReport = (format: 'pdf' | 'excel' | 'csv') => {
    // Mock export functionality
    console.log(`Exportando relatório em formato ${format}`);
    
    // Create mock data for export
    const reportData = {
      type: selectedReport,
      dateRange,
      project: selectedProject,
      generatedBy: user?.name,
      generatedAt: new Date().toISOString(),
      data: selectedReport === 'productivity' ? productivityData :
            selectedReport === 'projects' ? projectPerformance :
            selectedReport === 'time' ? timeTrackingData :
            teamPerformance
    };

    // Create a downloadable file
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `relatorio_${selectedReport}_${format}_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const shareReport = (method: 'email' | 'link') => {
    if (method === 'email') {
      window.location.href = `mailto:?subject=Relatório ${selectedReport}&body=Confira o relatório em anexo`;
    } else {
      navigator.clipboard.writeText(window.location.href);
      console.log('Link copiado para área de transferência');
    }
  };

  const renderProductivityReport = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Produtividade Diária
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {productivityData.reduce((sum, day) => sum + day.tasksCompleted, 0)}
                </div>
                <div className="text-sm text-gray-600">Tarefas Concluídas</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {Math.round(productivityData.reduce((sum, day) => sum + day.productivity, 0) / productivityData.length)}%
                </div>
                <div className="text-sm text-gray-600">Produtividade Média</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {productivityData.length}
                </div>
                <div className="text-sm text-gray-600">Dias Ativos</div>
              </div>
            </div>
            
            <div className="space-y-2">
              {productivityData.slice(-7).map((day, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 w-20">
                    {format(day.date, 'dd/MM', { locale: ptBR })}
                  </span>
                  <div className="flex-1 mx-3">
                    <Progress value={day.productivity} className="h-2" />
                  </div>
                  <span className="text-sm font-medium w-16 text-right">
                    {day.productivity}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="w-5 h-5" />
            Distribuição de Tarefas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.values(TaskStatus).map(status => {
              const count = tasks.filter(t => t.status === status).length;
              const percentage = tasks.length > 0 ? Math.round((count / tasks.length) * 100) : 0;
              
              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      status === TaskStatus.DONE ? 'bg-green-500' :
                      status === TaskStatus.IN_PROGRESS ? 'bg-blue-500' :
                      status === TaskStatus.REVIEW ? 'bg-yellow-500' :
                      'bg-gray-500'
                    }`} />
                    <span className="text-sm text-gray-600">
                      {status === TaskStatus.DONE ? 'Concluído' :
                       status === TaskStatus.IN_PROGRESS ? 'Em Andamento' :
                       status === TaskStatus.REVIEW ? 'Em Revisão' :
                       'A Fazer'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-16">
                      <Progress value={percentage} className="h-2" />
                    </div>
                    <span className="text-sm font-medium w-12 text-right">{count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderProjectsReport = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Performance dos Projetos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {projectPerformance.map(project => (
              <div key={project.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">{project.name}</h4>
                    <p className="text-sm text-gray-600">{project.description}</p>
                  </div>
                  <Badge variant="secondary" className={
                    project.status === ProjectStatus.COMPLETED ? 'bg-green-100 text-green-800' :
                    project.status === ProjectStatus.IN_PROGRESS ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }>
                    {project.status === ProjectStatus.COMPLETED ? 'Concluído' :
                     project.status === ProjectStatus.IN_PROGRESS ? 'Em Andamento' :
                     'Planejamento'}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">{project.completionRate}%</div>
                    <div className="text-xs text-gray-600">Conclusão</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">{project.tasksCount}</div>
                    <div className="text-xs text-gray-600">Tarefas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-600">{project.totalHours}h</div>
                    <div className="text-xs text-gray-600">Estimadas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-600">{project.efficiency}%</div>
                    <div className="text-xs text-gray-600">Eficiência</div>
                  </div>
                </div>
                
                <div className="mt-3">
                  <Progress value={project.completionRate} className="h-2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderTimeReport = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Tempo Trabalhado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {timeTrackingData.map((day, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 w-8">{day.day}</span>
                <div className="flex-1 mx-3">
                  <Progress value={(day.hours / 8) * 100} className="h-3" />
                </div>
                <span className="text-sm font-medium w-16 text-right">{day.hours}h</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Eficiência Semanal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {Math.round(timeTrackingData.reduce((sum, day) => sum + day.efficiency, 0) / timeTrackingData.length)}%
              </div>
              <div className="text-sm text-gray-600">Eficiência Média</div>
            </div>
            
            <div className="space-y-2">
              {timeTrackingData.map((day, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 w-8">{day.day}</span>
                  <div className="flex-1 mx-3">
                    <Progress value={day.efficiency} className="h-2" />
                  </div>
                  <span className="text-sm font-medium w-16 text-right">{day.efficiency}%</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderTeamReport = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Performance da Equipe
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {teamPerformance.map(member => (
              <div key={member.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <img 
                      src={member.avatar} 
                      alt={member.name}
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <h4 className="font-medium text-gray-900">{member.name}</h4>
                      <p className="text-sm text-gray-600">{member.tasksCount} tarefas atribuídas</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className={
                    member.productivity >= 90 ? 'bg-green-100 text-green-800' :
                    member.productivity >= 70 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }>
                    {member.productivity >= 90 ? 'Excelente' :
                     member.productivity >= 70 ? 'Bom' :
                     'Precisa melhorar'}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">{member.completionRate}%</div>
                    <div className="text-xs text-gray-600">Conclusão</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">{member.completedTasks}</div>
                    <div className="text-xs text-gray-600">Concluídas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-600">{member.totalHours}h</div>
                    <div className="text-xs text-gray-600">Trabalhadas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-600">{member.productivity}%</div>
                    <div className="text-xs text-gray-600">Produtividade</div>
                  </div>
                </div>
                
                <div className="mt-3">
                  <Progress value={member.productivity} className="h-2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderReportContent = () => {
    switch (selectedReport) {
      case 'productivity': return renderProductivityReport();
      case 'projects': return renderProjectsReport();
      case 'time': return renderTimeReport();
      case 'team': return renderTeamReport();
      default: return renderProductivityReport();
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Relatórios</h1>
          <p className="text-gray-600 mt-1">Análise detalhada de performance e produtividade</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={() => shareReport('email')}>
            <Mail className="w-4 h-4 mr-2" />
            Compartilhar
          </Button>
          <Button variant="outline" onClick={() => exportReport('pdf')}>
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Tipo de Relatório
              </label>
              <Select value={selectedReport} onValueChange={(value) => setSelectedReport(value as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="productivity">Produtividade</SelectItem>
                  <SelectItem value="projects">Projetos</SelectItem>
                  <SelectItem value="time">Tempo</SelectItem>
                  <SelectItem value="team">Equipe</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Período
              </label>
              <Select value={dateRange} onValueChange={(value) => setDateRange(value as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Esta Semana</SelectItem>
                  <SelectItem value="month">Este Mês</SelectItem>
                  <SelectItem value="quarter">Este Trimestre</SelectItem>
                  <SelectItem value="year">Este Ano</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Projeto
              </label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Projetos</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button className="w-full bg-gradient-to-r from-blue-500 to-purple-600">
                <BarChart3 className="w-4 h-4 mr-2" />
                Gerar Relatório
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Options */}
      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <FileText className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">Exportar Relatório</span>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={() => exportReport('pdf')}>
            <Download className="w-4 h-4 mr-1" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportReport('excel')}>
            <Download className="w-4 h-4 mr-1" />
            Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportReport('csv')}>
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
        </div>
      </div>

      {/* Report Content */}
      {renderReportContent()}
    </div>
  );
};